// src/pages/workflow/WorkFlowEdit.jsx
import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import {
  Save,
  Undo,
  Redo,
  PlusCircle,
  AlertTriangle,
  ArrowLeft,
  CornerUpLeft,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiCall } from "../../Apis/apiCall";
import LoadingComponent from "../../components/ui/LoadingComponent";
import { htmlComponents } from "../../components/workflow/HtmlComponent";
import { FieldConfigModal } from "../../components/workflow/FieldConfigModal";
import {
  CustomStep,
  WorkflowNameNode,
} from "../../components/workflow/CustomNode";
import { handleApiResponse, handleCustomResponse } from "../../utils/response";

// Utility Functions
const generateUniqueCode = (label, existingCodes) => {
  let baseCode =
    label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim() || "step";
  let uniqueCode = baseCode;
  let counter = 1;
  while (existingCodes.includes(uniqueCode)) {
    uniqueCode = `${baseCode}_${counter}`;
    counter++;
  }
  return uniqueCode;
};

// Step Update Modal
const StepUpdateModal = ({ isOpen, onClose, step, onSave }) => {
  const [stepLabel, setStepLabel] = useState(step?.label || "");
  const [stepLabelError, setStepLabelError] = useState("");

  useEffect(() => {
    if (step) {
      setStepLabel(step.label);
      setStepLabelError("");
    }
  }, [step]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!stepLabel.trim()) {
      setStepLabelError("Step label cannot be empty.");
      return;
    }
    onSave({ ...step, label: stepLabel });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md"
        style={{ width: "400px" }}
      >
        <h3 className="mb-24">Update Step</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <label className="form-label fw-bold">Step Label</label>
            <input
              type="text"
              className={`form-control ${stepLabelError ? "is-invalid" : ""}`}
              value={stepLabel}
              onChange={(e) => {
                setStepLabel(e.target.value);
                setStepLabelError("");
              }}
              required
            />
            {stepLabelError && (
              <div className="invalid-feedback">{stepLabelError}</div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              Update Step
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Generic Deletion Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md"
        style={{ minWidth: "400px" }}
      >
        <h5 className="mb-16">{title}</h5>
        <p>{message}</p>
        <div className="d-flex justify-content-end gap-2 mt-24">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Unsaved Changes Confirmation Modal
const UnsavedChangesModal = ({ isOpen, onClose, onSave, onDiscard }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md text-center"
        style={{ width: "450px" }}
      >
        <AlertTriangle size={48} className="text-warning-500 mx-auto mb-16" />
        <h5 className="mb-2">Unsaved Changes</h5>
        <p className="text-secondary-light">
          You have unsaved changes. Do you want to save them before leaving?
        </p>
        <div className="d-flex justify-content-center gap-3 mt-24">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onDiscard}
          >
            Discard
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary-600"
            onClick={onSave}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkFlowEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { workflow } = location.state || {};
  const workflowId = workflow?.workflow_id || null;
  const workflowName = workflow?.workflow_name || "No Workflow Selected";

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const [stepLabel, setStepLabel] = useState("");
  const [stepLabelError, setStepLabelError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [fieldConfigModal, setFieldConfigModal] = useState({
    isOpen: false,
    field: null,
    isEditMode: false,
    nodeId: null,
  });
  const [stepUpdateModal, setStepUpdateModal] = useState({
    isOpen: false,
    step: null,
    nodeId: null,
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    nodeId: null,
  });
  const [deletedNodes, setDeletedNodes] = useState([]);
  const [optionSources, setOptionSources] = useState({ master: [], api: [] });
  const [fieldNames, setFieldNames] = useState([]);
  const [stepSuggestions, setStepSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState(null);
  const initialWorkflowState = useRef(null);
  const isInitialLoad = useRef(true);

  // Ref to store static data
  const staticDataRef = useRef({
    stepSuggestions: [],
    optionSources: { master: [], api: [] },
    fieldNames: [],
  });

  const saveToHistory = useCallback(
    (currentNodes, currentEdges) => {
      const stateToSave = {
        nodes: JSON.parse(JSON.stringify(currentNodes)),
        edges: JSON.parse(JSON.stringify(currentEdges)),
      };
      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        return [...newHistory, stateToSave];
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex]
  );

  const recalculateLayout = useCallback((nodesToLayout) => {
    const workflowNameNode = nodesToLayout.find(
      (n) => n.type === "workflowName"
    );
    const customNodes = nodesToLayout
      .filter((n) => n.type === "custom")
      .sort((a, b) => a.data.step_order - b.data.step_order);

    let currentY = 100;
    const xLeft = 100;
    const xRight = 500;
    const baseNodeHeight = 150;
    const fieldHeight = 60;

    const laidOutNodes = customNodes.map((node, index) => {
      const isEven = index % 2 === 0;
      const xPos = isEven ? xLeft : xRight;

      const nodeHeight =
        baseNodeHeight + (node.data.fields?.length || 0) * fieldHeight;

      const updatedNode = {
        ...node,
        position: { x: xPos, y: currentY },
        data: { ...node.data, step_order: index + 1 },
      };

      currentY += nodeHeight + 50;
      return updatedNode;
    });

    const finalNodes = workflowNameNode
      ? [workflowNameNode, ...laidOutNodes]
      : laidOutNodes;

    const newEdges = [];
    if (laidOutNodes.length > 0) {
      if (workflowNameNode) {
        newEdges.push({
          id: `edge-workflowName-${laidOutNodes[0].id}`,
          source: "workflowName",
          target: laidOutNodes[0].id,
          animated: true,
          style: { stroke: "#4b5563", strokeWidth: 2 },
        });
      }
      for (let i = 0; i < laidOutNodes.length - 1; i++) {
        newEdges.push({
          id: `edge-${laidOutNodes[i].id}-${laidOutNodes[i + 1].id}`,
          source: laidOutNodes[i].id,
          target: laidOutNodes[i + 1].id,
          animated: true,
          style: { stroke: "#4b5563", strokeWidth: 2 },
        });
      }
    }

    return { finalNodes, finalEdges: newEdges };
  }, []);

  const updateWorkflowState = useCallback(
    (nodesToUpdate, saveHistory = true) => {
      const { finalNodes, finalEdges } = recalculateLayout(nodesToUpdate);
      setNodes(finalNodes);
      setEdges(finalEdges);
      if (saveHistory) {
        saveToHistory(finalNodes, finalEdges);
      }
    },
    [recalculateLayout, setNodes, setEdges, saveToHistory]
  );

  useEffect(() => {
    if (initialWorkflowState.current) {
      const currentState = JSON.stringify({ nodes, edges });
      if (currentState !== initialWorkflowState.current) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }
  }, [nodes, edges]);

  const handleNavigation = (target) => {
    if (isDirty) {
      setNavigationTarget(target);
      setShowUnsavedModal(true);
      if (historyIndex > 0) {
        setNavigationTarget(target);
        setShowUnsavedModal(true);
      } else {
        navigate(-1);
      }
    } else {
      navigate(target);
    }
  };

  const handleDiscard = () => {
    setShowUnsavedModal(false);
    navigate(navigationTarget);
  };

  const handleSaveAndNavigate = async () => {
    await handleSaveWorkflow();
    setShowUnsavedModal(false);
    navigate(navigationTarget);
  };

  // const nodeTypes = {
  //   custom: (props) => (
  //     <CustomStep
  //       {...props}
  //       data={{
  //         ...props.data,
  //         onUpdateFields: handleUpdateFields,
  //         onRemoveNode: (id) =>
  //           setDeleteConfirmModal({ isOpen: true, nodeId: id }),
  //         onEditField: handleEditField,
  //         onDeleteField: handleDeleteField,
  //         onEditStep: handleEditStep,
  //       }}
  //     />
  //   ),
  //   workflowName: WorkflowNameNode,
  // };
  const handleMoveFieldToOtherStep = useCallback(
  (fieldId, sourceNodeId, targetNodeId) => {
    if (sourceNodeId === targetNodeId) return;

    setNodes((nds) => {
      const sourceNode = nds.find((node) => node.id === sourceNodeId);
      const targetNode = nds.find((node) => node.id === targetNodeId);

      if (!sourceNode || !targetNode) return nds;

      const fieldToMove = sourceNode.data.fields.find((f) => f.id === fieldId);
      if (!fieldToMove) return nds;

      // Field ko source se remove karo
      const updatedSourceFields = sourceNode.data.fields
        .filter((f) => f.id !== fieldId)
        .map((f, index) => ({ ...f, order: index + 1 }));

      // Field ko target mein add karo
      const newFieldOrder = targetNode.data.fields.length + 1;
      const updatedTargetFields = [
        ...targetNode.data.fields,
        {
          ...fieldToMove,
          id: `moved-${uuidv4()}`,
          field_id: null,
          order: newFieldOrder,
        },
      ];

      const newNodes = nds.map((node) => {
        if (node.id === sourceNodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              fields: updatedSourceFields,
            },
          };
        }
        if (node.id === targetNodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              fields: updatedTargetFields,
            },
          };
        }
        return node;
      });

      const { finalNodes, finalEdges } = recalculateLayout(newNodes);
      setEdges(finalEdges);
      saveToHistory(finalNodes, finalEdges);
      return finalNodes;
    });

    handleCustomResponse("success", "Field moved to another step.");
  },
  [setNodes, setEdges, saveToHistory, recalculateLayout]
);
const nodeTypes = {
  custom: (props) => (
    <CustomStep
      {...props}
      data={{
        ...props.data,
        onUpdateFields: handleUpdateFields,
        onMoveFieldToOtherStep: handleMoveFieldToOtherStep, // ✅ Add this line
        onRemoveNode: (id) =>
          setDeleteConfirmModal({ isOpen: true, nodeId: id }),
        onEditField: handleEditField,
        onDeleteField: handleDeleteField,
        onEditStep: handleEditStep,
      }}
    />
  ),
  workflowName: WorkflowNameNode,
};
  const fetchStaticData = useCallback(async () => {
    try {
      const [stepSuggestionsRes, apiRes, fieldNamesRes] = await Promise.all([
        apiCall("get", `/workflow/template/step-suggestions`, null, navigate),
        apiCall("get", `/get-option-source-data/2`, null, navigate),
        apiCall("get", `/workflow-field-names`, null, navigate),
      ]);

      if (stepSuggestionsRes.data.success) {
        staticDataRef.current.stepSuggestions = stepSuggestionsRes.data.data;
        setStepSuggestions(stepSuggestionsRes.data.data);
      }
      staticDataRef.current.optionSources = {
        master: [],
        api: apiRes.data.data,
      };
      staticDataRef.current.fieldNames = fieldNamesRes.data.data;
      setOptionSources(staticDataRef.current.optionSources);
      setFieldNames(staticDataRef.current.fieldNames);
    } catch (error) {
      handleCustomResponse(
        "error",
        "Failed to load static configuration data."
      );
    }
  }, [navigate]);

  const fetchWorkFlow = useCallback(async () => {
    if (!workflowId) {
      handleCustomResponse("error", "No workflow selected");
      navigate(-1);
      return;
    }
    setIsLoading(true);
    try {
      const url = `/workflow-steps-fields-v2/${workflowId}`;
      const res = await apiCall("get", url, null, navigate);
      const workflowData = res.data.data;

      const workflowNameNode = {
        id: "workflowName",
        type: "workflowName",
        position: { x: 100, y: 0 },
        data: { label: workflowName },
        draggable: false,
        selectable: false,
      };

      const steps = workflowData.steps || [];
      const newNodes = [
        workflowNameNode,
        ...steps.map((step, index) => ({
          id: String(step.step_id),
          type: "custom",
          position: { x: 0, y: 0 },
          data: {
            step_id: step.step_id,
            code: step.step_code,
            label: step.step_label,
            step_order: index + 1,
            fields:
              step.fields?.map((field, idx) => ({
                id: String(field.field_id || `client-${Date.now()}-${idx}`),
                field_id: field.field_id,
                name: field.name,
                label: field.label,
                type: field.type,
                required: field.required,
                validation: field.validation,
                options:
                  field.options && field.options !== "[]"
                    ? JSON.parse(field.options)
                    : null,
                options_source: field.options_source,
                options_label_field: field.options_label_field,
                options_value_field: field.options_value_field,
                order: field.order || idx + 1,
              })) || [],
          },
        })),
      ];

      const { finalNodes, finalEdges } = recalculateLayout(newNodes);
      setNodes(finalNodes);
      setEdges(finalEdges);

      const initialState = JSON.stringify({
        nodes: finalNodes,
        edges: finalEdges,
      });
      initialWorkflowState.current = initialState;
      setIsDirty(false);
      saveToHistory(finalNodes, finalEdges);
    } catch (error) {
      handleCustomResponse("error", "Failed to fetch workflow data.");
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, workflowName, navigate, recalculateLayout, saveToHistory]);

  useEffect(() => {
    fetchStaticData();
    fetchWorkFlow();
  }, []);

  const validateStep = (label, excludeNodeId = null) => {
    if (!label.trim()) return "Step label is required.";
    if (
      nodes.some(
        (node) => node.data.label === label && node.id !== excludeNodeId
      )
    )
      return "Step label must be unique.";
    return "";
  };

  const handleAddStep = () => {
    const error = validateStep(stepLabel);
    if (error) {
      setStepLabelError(error);
      return;
    }
    setStepLabelError("");

    const customNodes = nodes.filter((n) => n.type === "custom");
    const newCode = generateUniqueCode(
      stepLabel,
      nodes.map((n) => n.data.code)
    );

    const newNode = {
      id: `node-${uuidv4()}`,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        step_id: null,
        code: newCode,
        label: stepLabel,
        step_order: customNodes.length + 1,
        fields: [],
      },
    };

    updateWorkflowState([...nodes, newNode]);
    setStepLabel("");
    handleCustomResponse("success", `Step "${stepLabel}" added.`);
  };

  const handleAddSuggestedStep = (suggestion) => {
    const error = validateStep(suggestion.template_label);
    if (error) {
      handleCustomResponse("error", error);
      return;
    }

    const customNodes = nodes.filter((n) => n.type === "custom");
    const newCode = generateUniqueCode(
      suggestion.template_label,
      nodes.map((n) => n.data.code)
    );

    const newNode = {
      id: `node-${uuidv4()}`,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        step_id: null,
        code: newCode,
        label: suggestion.template_label,
        step_order: customNodes.length + 1,
        fields: suggestion.fields.map((field, idx) => ({
          id: `new-${uuidv4()}`,
          field_id: null,
          name: field.name,
          label: field.label,
          type: field.type,
          required: field.required,
          validation: field.validation,
          options: field.options ? JSON.parse(field.options) : null,
          options_source: field.options_source,
          options_label_field: field.options_label_field,
          options_value_field: field.options_value_field,
          order: idx + 1,
        })),
      },
    };

    updateWorkflowState([...nodes, newNode]);
    handleCustomResponse(
      "success",
      `Suggested step "${suggestion.template_label}" added.`
    );
  };

  const handleEditStep = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (node) setStepUpdateModal({ isOpen: true, step: node.data, nodeId });
  };

  const handleSaveStep = (updatedStep) => {
    const error = validateStep(updatedStep.label, stepUpdateModal.nodeId);
    if (error) {
      handleCustomResponse("error", error);
      return;
    }

    const newCode = generateUniqueCode(
      updatedStep.label,
      nodes
        .filter((n) => n.id !== stepUpdateModal.nodeId)
        .map((n) => n.data.code)
    );

    const updatedNodes = nodes.map((node) =>
      node.id === stepUpdateModal.nodeId
        ? {
            ...node,
            data: { ...node.data, label: updatedStep.label, code: newCode },
          }
        : node
    );

    setNodes(updatedNodes);
    saveToHistory(updatedNodes, edges);
    handleCustomResponse("success", `Step "${updatedStep.label}" updated.`);
  };

  const handleSaveFieldConfig = (updatedField) => {
    const { nodeId, isEditMode } = fieldConfigModal;
    setNodes((nds) => {
      const newNodes = nds.map((node) => {
        if (node.id === nodeId) {
          const isDuplicate = node.data.fields.some(
            (f) => f.name === updatedField.name && f.id !== updatedField.id
          );
          if (isDuplicate) {
            handleCustomResponse(
              "error",
              "Field name already exists in this step."
            );
            return node;
          }

          const newFields = isEditMode
            ? node.data.fields.map((f) =>
                f.id === updatedField.id
                  ? { ...updatedField, order: f.order }
                  : f
              )
            : [
                ...node.data.fields,
                {
                  ...updatedField,
                  id: `new-${uuidv4()}`,
                  field_id: null,
                  order: node.data.fields.length + 1,
                },
              ];

          const sortedFields = newFields
            .sort((a, b) => a.order - b.order)
            .map((f, index) => ({ ...f, order: index + 1 }));

          return { ...node, data: { ...node.data, fields: sortedFields } };
        }
        return node;
      });

      const { finalNodes, finalEdges } = recalculateLayout(newNodes);
      setEdges(finalEdges);
      saveToHistory(finalNodes, finalEdges);
      return finalNodes;
    });
    handleCustomResponse(
      "success",
      isEditMode ? "Field updated" : "Field added"
    );
  };

  const handleEditField = (field, nodeId) => {
    setFieldConfigModal({ isOpen: true, field, isEditMode: true, nodeId });
  };

  const handleDeleteField = useCallback(
    (fieldId, nodeId) => {
      setNodes((nds) => {
        const newNodes = nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  fields: node.data.fields
                    .filter((f) => f.id !== fieldId)
                    .map((f, index) => ({ ...f, order: index + 1 })),
                },
              }
            : node
        );

        const { finalNodes, finalEdges } = recalculateLayout(newNodes);
        setEdges(finalEdges);
        saveToHistory(finalNodes, finalEdges);
        return finalNodes;
      });
      handleCustomResponse("success", "Field successfully removed");
    },
    [setNodes, setEdges, saveToHistory, recalculateLayout]
  );

  const handleUpdateFields = useCallback(
    (nodeId, reorderedFields) => {
      const sortedFields = reorderedFields.map((field, index) => ({
        ...field,
        order: index + 1,
      }));
      setNodes((nds) => {
        const newNodes = nds.map((node) => {
          if (node.id === nodeId) {
            return { ...node, data: { ...node.data, fields: sortedFields } };
          }
          return node;
        });

        const { finalNodes, finalEdges } = recalculateLayout(newNodes);
        setEdges(finalEdges);
        saveToHistory(finalNodes, finalEdges);
        return finalNodes;
      });
      handleCustomResponse("success", "Fields reordered within step.");
    },
    [setNodes, setEdges, saveToHistory, recalculateLayout]
  );

  const handleRemoveNode = useCallback(
    (nodeId) => {
      // Find the node to remove
      const nodeToRemove = nodes.find((node) => node.id === nodeId);
      if (!nodeToRemove) {
        handleCustomResponse("error", "Step not found.");
        return;
      }

      // Add the node to deletedNodes for potential restoration
      setDeletedNodes((prev) => [...prev, nodeToRemove]);

      // Remove the node from the nodes array
      const remainingNodes = nodes.filter((node) => node.id !== nodeId);

      // Update the workflow state with recalculated layout
      updateWorkflowState(remainingNodes);

      // Clear selectedNode if it was the deleted node
      if (selectedNode?.id === nodeId) {
        setSelectedNode(null);
      }

      // Show success message
      handleCustomResponse(
        "info",
        `Step "${nodeToRemove.data.label}" moved to trash.`
      );
    },
    [nodes, selectedNode, updateWorkflowState]
  );

  const handleRestoreNode = useCallback(
    (nodeId) => {
      const nodeToRestore = deletedNodes.find((node) => node.id === nodeId);
      if (!nodeToRestore) return;

      const error = validateStep(nodeToRestore.data.label);
      if (error) {
        handleCustomResponse("error", `Cannot restore: ${error}`);
        return;
      }

      const customNodes = nodes.filter((n) => n.type === "custom");
      const restoredNode = {
        ...nodeToRestore,
        data: {
          ...nodeToRestore.data,
          step_id: null,
          step_order: customNodes.length + 1,
          fields: nodeToRestore.data.fields.map((field) => ({
            ...field,
            field_id: null,
            id: `new-${uuidv4()}`,
          })),
        },
      };

      updateWorkflowState([...nodes, restoredNode]);
      setDeletedNodes((prev) => prev.filter((node) => node.id !== nodeId));
      handleCustomResponse(
        "success",
        `Step "${nodeToRestore.data.label}" restored.`
      );
    },
    [nodes, deletedNodes, updateWorkflowState, validateStep]
  );

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      setHistoryIndex(newIndex);
      handleCustomResponse("info", "Action undone");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(newIndex);
      handleCustomResponse("info", "Action redone");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleSaveWorkflow = async () => {
    setSaveStatus("saving");
    setIsLoading(true);
    try {
      const sortedNodes = [...nodes]
        .filter((node) => node.type === "custom")
        .sort((a, b) => a.data.step_order - b.data.step_order);

      const workflowData = {
        workflow_id: workflowId,
        steps: sortedNodes.map((node, stepIdx) => ({
          step_id: node.data.step_id || null,
          step_code: node.data.code,
          step_label: node.data.label,
          step_order: stepIdx + 1,
          fields: node.data.fields
            .sort((a, b) => a.order - b.order)
            .map((field, fieldIdx) => ({
              field_id: field.field_id || null,
              name: field.name || "",
              label: field.label || "",
              type: field.type || "",
              required: field.required || false,
              validation: field.validation || "",
              options: field.options ? field.options : null,
              options_source: field.options_source || null,
              options_label_field: field.options_label_field || null,
              options_value_field: field.options_value_field || null,
              field_order: fieldIdx + 1,
            })),
        })),
      };

      const res = await apiCall(
        "post",
        "/insert-workflow-step-fields",
        workflowData,
        navigate
      );
      handleApiResponse(res.data, "Save WorkFlow", "POST");
      if (res.data.success) {
        setSaveStatus("success");
        await fetchWorkFlow();
      } else {
        throw new Error(res.data.message || "Failed to save workflow");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to Save WorkFlow", "POST");
      setSaveStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const onDragStart = (event, component) => {
    event.dataTransfer.setData(
      "application/reactflow",
      JSON.stringify(component)
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || !selectedNode) {
        handleCustomResponse(
          "warning",
          "Please select a step to add fields to."
        );
        return;
      }
      let component;
      try {
        component = JSON.parse(
          event.dataTransfer.getData("application/reactflow")
        );
      } catch (error) {
        handleCustomResponse("error", "Invalid component data.");
        return;
      }
      if (!component || !component.type) {
        handleCustomResponse("error", "Component type is missing.");
        return;
      }
      const nextFieldOrder = selectedNode.data.fields.length;
      setFieldConfigModal({
        isOpen: true,
        field: { ...component, order: nextFieldOrder },
        isEditMode: false,
        nodeId: selectedNode.id,
      });
      handleCustomResponse("info", `Configuring new ${component.type} field.`);
    },
    [reactFlowInstance, selectedNode]
  );

  const onNodeDragStop = useCallback(
    (event, draggedNode) => {
      if (draggedNode.type === "workflowName") return;

      const sortedByY = nodes
        .filter((n) => n.type === "custom")
        .sort((a, b) => a.position.y - b.position.y)
        .map((node, index) => ({
          ...node,
          data: { ...node.data, step_order: index + 1 },
        }));

      const workflowNameNode = nodes.find((n) => n.type === "workflowName");
      const nodesForLayout = workflowNameNode
        ? [workflowNameNode, ...sortedByY]
        : sortedByY;

      updateWorkflowState(nodesForLayout);
    },
    [nodes, updateWorkflowState]
  );

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) => {
        const newEdge = {
          ...params,
          animated: true,
          style: { stroke: "#4b5563", strokeWidth: 2 },
        };
        return addEdge(newEdge, eds);
      }),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    if (node.type !== "workflowName") setSelectedNode(node);
  }, []);

  if (isLoading && isInitialLoad.current) {
    return <LoadingComponent />;
  }

  return (
    <>
      <div className="d-lg-none text-center p-4 min-vh-100 d-flex flex-column justify-content-center align-items-center bg-neutral-50">
        <div className="max-w-md">
          <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
            Device Compatibility
          </h3>
          <p className="text-neutral-600 mb-6 p-5">
            This workflow editor is optimized for larger screens (large devices
            and up). Please use a desktop or larger tablet for the best
            experience.
          </p>
          <button
            className="btn btn-primary-600 px-18 py-18 rounded-md transition-colors duration-200 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => navigate(-1)}
            aria-label="Go back to previous page"
          >
            <span className="d-flex align-items-center justify-content-center gap-2">
              <ArrowLeft size={18} />
              Go Back
            </span>
          </button>
        </div>
      </div>
      <div className="d-none d-lg-flex flex-column vh-100">
        <header className="row p-3 m-0">
          <div className="col d-flex align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span onClick={() => handleNavigation("/workflows")}>
                <ArrowLeft size={24} />
              </span>
            </div>
          </div>
          <div className="col-6 d-flex justify-content-end align-items-center gap-3">
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                title="Undo"
              >
                <Undo size={16} />
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                title="Redo"
              >
                <Redo size={16} />
              </button>
            </div>
            <button
              className="btn btn-primary-600"
              onClick={handleSaveWorkflow}
              disabled={isLoading || saveStatus === "saving"}
            >
              <div className="d-flex align-items-center gap-2">
                <Save size={16} />
                {isLoading || saveStatus === "saving"
                  ? "Saving..."
                  : "Save Workflow"}
              </div>
            </button>
          </div>
        </header>
        <div className="row g-0 flex-grow-1" style={{ minHeight: 0 }}>
          <aside className="col-12 col-lg-2 border-end p-24 d-flex flex-column">
            <div className="flex-grow-1" style={{ overflowY: "auto" }}>
              <p className="text-primary-600 fw-bold mb-16">Add New Step</p>
              <div className="mb-16">
                <input
                  type="text"
                  className={`form-control ${
                    stepLabelError ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Step Label"
                  value={stepLabel}
                  onChange={(e) => {
                    setStepLabel(e.target.value);
                    setStepLabelError("");
                  }}
                  disabled={isLoading}
                />
                {stepLabelError && (
                  <div className="invalid-feedback">{stepLabelError}</div>
                )}
              </div>
              <button
                className="btn btn-primary-600 w-100 mb-24"
                onClick={handleAddStep}
                disabled={isLoading}
              >
                Add Step
              </button>
              <hr className="my-24" />
              <p className="text-primary-600 fw-bold mb-16">Deleted Steps</p>
              {deletedNodes.length > 0 ? (
                <div className="deleted-steps-list">
                  {deletedNodes.map((node) => (
                    <div
                      key={node.id}
                      className="deleted-step-item d-flex align-items-center text-center"
                    >
                      <span className="text-sm text-truncate flex-grow-1 pe-2">
                        {node.data.label}
                      </span>
                      <span
                        className="cursor-pointer"
                        onClick={() => handleRestoreNode(node.id)}
                        title="Restore Step"
                      >
                        <CornerUpLeft size={14} />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="small text-secondary-light mt-2">
                  No deleted steps.
                </p>
              )}
            </div>
          </aside>
          <main
            className="col-12 col-lg-8 d-flex flex-column"
            ref={reactFlowWrapper}
          >
            <div className="p-16 overflow-x-auto suggestion-bar">
              <div className="d-flex gap-2">
                {stepSuggestions.length > 0 ? (
                  stepSuggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="suggestion-chip"
                      onClick={() => handleAddSuggestedStep(suggestion)}
                    >
                      <PlusCircle size={14} className="me-1" />
                      {suggestion.template_label}
                    </div>
                  ))
                ) : (
                  <span className="text-secondary-light small">
                    No suggested steps available.
                  </span>
                )}
              </div>
            </div>
            <div className="flex-grow-1" style={{ minHeight: 0 }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesState}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeDragStop={onNodeDragStop}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                fitView
                nodesDraggable={!isLoading}
              >
                <MiniMap
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Controls />
                <Background color="#e2e8f0" gap={20} />
              </ReactFlow>
            </div>
          </main>
          <aside
            className="col-12 col-lg-2 border-start p-24"
            style={{ overflowY: "auto" }}
          >
            <p className="text-primary-600 fw-bold mb-8">
              Available Components
            </p>
            {selectedNode ? (
              <>
                <p className="small mb-16">
                  Drag a field to: <strong>{selectedNode.data.label}</strong>
                </p>
                <div className="component-list">
                  {htmlComponents.map((component) => {
                    const IconComponent = component.icon;
                    return (
                      <button
                        key={component.step_field_id}
                        className="component-btn"
                        draggable
                        onDragStart={(event) => onDragStart(event, component)}
                      >
                        <IconComponent size={16} />
                        <span className="text-sm small">
                          {component.displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <p className="small text-secondary-light mb-16">
                  Select a step to add fields.
                </p>
                <div className="component-list">
                  {htmlComponents.map((component) => {
                    const IconComponent = component.icon;
                    return (
                      <button
                        key={component.step_field_id}
                        className="component-btn"
                        disabled
                      >
                        <IconComponent size={16} />
                        <span className="text-sm small">
                          {component.displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </aside>
        </div>
        <FieldConfigModal
          isOpen={fieldConfigModal.isOpen}
          onClose={() =>
            setFieldConfigModal({
              isOpen: false,
              field: null,
              isEditMode: false,
              nodeId: null,
            })
          }
          field={fieldConfigModal.field}
          onSave={handleSaveFieldConfig}
          isEditMode={fieldConfigModal.isEditMode}
          fieldNames={fieldNames}
          optionSources={optionSources}
        />
        <StepUpdateModal
          isOpen={stepUpdateModal.isOpen}
          onClose={() =>
            setStepUpdateModal({ isOpen: false, step: null, nodeId: null })
          }
          step={stepUpdateModal.step}
          onSave={handleSaveStep}
        />
        <DeleteConfirmModal
          isOpen={deleteConfirmModal.isOpen}
          onClose={() => setDeleteConfirmModal({ isOpen: false, nodeId: null })}
          onConfirm={() => {
            handleRemoveNode(deleteConfirmModal.nodeId);
            setDeleteConfirmModal({ isOpen: false, nodeId: null });
          }}
          title="Confirm Deletion"
          message="Are you sure you want to delete this step? You can restore it from the 'Deleted Steps' list."
        />
        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          onClose={() => setShowUnsavedModal(false)}
          onSave={handleSaveAndNavigate}
          onDiscard={handleDiscard}
        />
      </div>
    </>
  );
};

export default WorkFlowEdit;